import { createFileRoute, Link } from "@tanstack/react-router";

const LAST_UPDATED = "July 27, 2026";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      {
        title: "Terms of Service — FlowState Automate",
      },
      {
        name: "description",
        content:
          "The terms governing your use of the FlowState Automate application, including accounts, acceptable use, integrations, billing, and liability.",
      },
    ],
  }),
  component: TermsOfServicePage,
});

function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-4"
          >
            ← Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Terms of Service
          </h1>
          <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <p className="text-foreground leading-relaxed">
              Please read these Terms of Service (&quot;Terms&quot;) carefully
              before using the FlowState Automate application and related
              services (the &quot;Service&quot;), operated by FlowState Automate
              Pty Ltd. See also our{" "}
              <Link
                to="/privacy"
                className="text-primary hover:text-primary/80 underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-foreground leading-relaxed">
              By accessing or using the Service, you acknowledge that you have
              read, understood, and agree to be bound by these Terms. If you do
              not agree with any part of these Terms, you must not use the
              Service. If you use the Service on behalf of an organisation, you
              represent that you have authority to bind that organisation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              2. Accounts and Organisations
            </h2>
            <p className="text-foreground leading-relaxed">
              You are responsible for safeguarding your credentials and for all
              activity under your account. Data in the Service is scoped to an
              organisation, and organisation administrators control member
              access and roles. You must provide accurate information and keep
              it current.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              3. Third-Party Integrations
            </h2>
            <p className="text-foreground leading-relaxed">
              The Service can connect to third-party products such as Google
              Workspace, Xero, and Stripe. When you authorise an integration,
              you permit us to access and process data from that product to
              deliver the connected features. Your use of those products remains
              subject to their own terms, and you may disconnect an integration
              at any time in your settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              4. AI Features and Credentials
            </h2>
            <p className="text-foreground leading-relaxed">
              The Service includes AI features that may run on credentials you
              provide, such as a Google account connection or your own model API
              key. You are responsible for any usage costs charged by your model
              provider. AI output can be inaccurate or incomplete, so you should
              review it before relying on it for business, financial, or legal
              decisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              5. Acceptable Use
            </h2>
            <p className="text-foreground leading-relaxed mb-3">
              You agree not to use the Service:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground">
              <li>For any unlawful purpose or in violation of applicable law</li>
              <li>To infringe the intellectual property or privacy rights of others</li>
              <li>To upload or transmit malicious code</li>
              <li>
                To attempt to gain unauthorised access to the Service, other
                accounts, or connected systems
              </li>
              <li>
                To send unsolicited bulk messages or otherwise abuse connected
                email or messaging integrations
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              6. Your Content
            </h2>
            <p className="text-foreground leading-relaxed">
              You retain ownership of the data and content you submit to the
              Service. You grant us a limited licence to host, process, and
              transmit that content solely to operate and improve the Service
              for you, including passing it to the integrations and model
              providers you enable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              7. Subscriptions and Billing
            </h2>
            <p className="text-foreground leading-relaxed">
              Paid plans are billed in advance on the interval shown at
              purchase. Unless stated otherwise, fees are non-refundable except
              where required by law, including under the Australian Consumer
              Law. We may change pricing with reasonable notice; changes take
              effect at your next renewal.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              8. Intellectual Property
            </h2>
            <p className="text-foreground leading-relaxed">
              The Service, including its software, design, text, graphics, and
              logos, is the property of FlowState Automate Pty Ltd or its
              licensors and is protected by copyright, trademark, and other
              laws. You may not copy, modify, distribute, or create derivative
              works from the Service without our written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              9. Disclaimers
            </h2>
            <p className="text-foreground leading-relaxed">
              Except for guarantees that cannot be excluded under the Australian
              Consumer Law, the Service is provided on an &quot;as is&quot; and
              &quot;as available&quot; basis without warranties of any kind,
              including merchantability, fitness for a particular purpose,
              non-infringement, or uninterrupted availability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              10. Limitation of Liability
            </h2>
            <p className="text-foreground leading-relaxed">
              To the maximum extent permitted by law, FlowState Automate Pty
              Ltd and its directors, employees, and agents are not liable for
              indirect, incidental, special, consequential, or punitive damages,
              including loss of profits or data. Our total liability for any
              claim relating to the Service will not exceed the amount you paid
              us in the twelve months before the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              11. Termination
            </h2>
            <p className="text-foreground leading-relaxed">
              You may stop using the Service and close your account at any
              time. We may suspend or terminate access if you breach these
              Terms or if required to protect the Service or other users. On
              termination, you may export your data for a reasonable period
              before deletion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              12. Changes to These Terms
            </h2>
            <p className="text-foreground leading-relaxed">
              We may update these Terms from time to time. Material changes will
              be communicated through the Service or by email. Continued use
              after changes take effect constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              13. Governing Law
            </h2>
            <p className="text-foreground leading-relaxed">
              These Terms are governed by the laws of Victoria, Australia,
              without regard to conflict of law principles. You submit to the
              exclusive jurisdiction of the courts of Victoria for any disputes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              14. Contact Us
            </h2>
            <p className="text-foreground leading-relaxed">
              FlowState Automate Pty Ltd, 440 Collins Street, Melbourne, VIC
              3000, Australia. Questions about these Terms can be sent to{" "}
              <a
                href="mailto:hello@flowstateautomate.ai"
                className="text-primary hover:text-primary/80 underline"
              >
                hello@flowstateautomate.ai
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
